import React from 'react';
import { DeckSlideWrapper } from '../DeckSlideWrapper';
import { TrendingUp, PieChart, ShieldAlert, Scale } from 'lucide-react';

export const Slide05MarketOpportunity: React.FC = () => {
  return (
    <DeckSlideWrapper
      slideNumber={5}
      totalSlides={12}
      category="Market Opportunity"
      title="A $35B+ Market with Urgent Regulatory Tailwinds"
      subtitle="Enterprise AI security is transitioning from an optional experiment to a mandatory CISO compliance line-item."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 sm:gap-5 items-stretch">
        {/* Left 3 Market Tiers */}
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3.5">
          {/* TAM */}
          <div className="p-3.5 sm:p-4 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex flex-col justify-between">
            <div>
              <div className="text-[11px] font-mono text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider mb-1">
                Total Addressable (TAM)
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-amber-600 dark:text-gold-400 font-mono my-1 sm:my-2">
                $35.4B
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Global Enterprise AI Governance, Security, and Compliance Market by 2030.
              </p>
            </div>
            <div className="mt-3 pt-2 border-t border-slate-200 dark:border-navy-700 font-mono text-[11px] text-emerald-600 dark:text-emerald-400 font-bold">
              +42.6% CAGR
            </div>
          </div>

          {/* SAM */}
          <div className="p-3.5 sm:p-4 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex flex-col justify-between">
            <div>
              <div className="text-[11px] font-mono text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider mb-1">
                Serviceable (SAM)
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white font-mono my-1 sm:my-2">
                $8.5B
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Enterprise AI Gateways, LLMOps Middleware, and AI Cost Accounting.
              </p>
            </div>
            <div className="mt-3 pt-2 border-t border-slate-200 dark:border-navy-700 font-mono text-[11px] text-blue-600 dark:text-blue-400 font-bold">
              Mid &amp; Large Enterprise
            </div>
          </div>

          {/* SOM */}
          <div className="p-3.5 sm:p-4 rounded-2xl bg-gold-500/10 dark:bg-gold-500/15 border border-gold-500/30 shadow-sm flex flex-col justify-between">
            <div>
              <div className="text-[11px] font-mono text-gold-800 dark:text-gold-400 font-bold uppercase tracking-wider mb-1">
                Target Initial (SOM)
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-gold-800 dark:text-gold-400 font-mono my-1 sm:my-2">
                $450M
              </div>
              <p className="text-xs text-slate-700 dark:text-slate-200">
                Regulated industries (Fintech, HealthTech, LegalTech, Defense) needing self-hosted DLP.
              </p>
            </div>
            <div className="mt-3 pt-2 border-t border-gold-500/30 font-mono text-[11px] text-gold-800 dark:text-gold-400 font-bold">
              Year 1–3 Focus
            </div>
          </div>
        </div>

        {/* Right 3 Market Catalysts */}
        <div className="lg:col-span-5 flex flex-col justify-between p-4 sm:p-4.5 rounded-2xl bg-slate-900 dark:bg-navy-950 text-slate-200 border border-slate-700 dark:border-navy-700 shadow-surface space-y-2.5 sm:space-y-3">
          <div className="flex items-center gap-2 border-b border-slate-800 dark:border-navy-800 pb-2">
            <TrendingUp className="w-4 h-4 text-gold-400" />
            <h4 className="font-mono text-xs font-bold text-white uppercase tracking-wider">
              3 Irreversible Market Drivers
            </h4>
          </div>

          <div className="space-y-2.5 sm:space-y-3">
            <div className="flex items-start gap-2.5">
              <Scale className="w-4 h-4 text-gold-400 shrink-0 mt-0.5" />
              <div>
                <strong className="text-xs text-white block">1. Regulatory Enforcement (EU AI Act, SEC, HIPAA)</strong>
                <p className="text-[11px] text-slate-400 leading-normal">
                  Heavy fines for unmonitored AI processing. Enterprises need auditable, compliant token routes.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2.5">
              <PieChart className="w-4 h-4 text-gold-400 shrink-0 mt-0.5" />
              <div>
                <strong className="text-xs text-white block">2. Model Proliferation &amp; Fragmentation</strong>
                <p className="text-[11px] text-slate-400 leading-normal">
                  Enterprises use 4+ LLMs concurrently. A centralized gateway is essential to avoid vendor lock-in.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2.5">
              <ShieldAlert className="w-4 h-4 text-gold-400 shrink-0 mt-0.5" />
              <div>
                <strong className="text-xs text-white block">3. 82% of CISOs Have Dedicated AI Budgets</strong>
                <p className="text-[11px] text-slate-400 leading-normal">
                  Security budgets are shifting toward LLM firewalling, prompt DLP, and cryptographic identity attestation.
                </p>
              </div>
            </div>
          </div>

          <div className="p-2.5 rounded-lg bg-slate-950 dark:bg-navy-900 border border-slate-800 dark:border-navy-800 text-[11px] font-mono text-gold-300 flex items-center justify-between">
            <span>Market Window:</span>
            <strong className="text-white">Now through 2028 land grab</strong>
          </div>
        </div>
      </div>
    </DeckSlideWrapper>
  );
};
